import { inject, provide, InjectionKey, Ref, ref } from "vue";

export type ToastOption = {
    useUnsafeHTMLMessage?: boolean,
    message: string,
    timeout: number,
    title: string,
};

const inner = Symbol("TOAST_INNER");

type Toast = ToastOption & {
    close: () => void,
    touch: () => void,
    id: number,
    created: Date,
    [inner]: {
        timeoutHandle: ReturnType<typeof setTimeout>,
    }
};

type ToastData = {
    toasts: Ref<Array<Toast>>,
    counter: number,
};

const toastSymbol: InjectionKey<ToastData> = Symbol("TOAST_INJECT");

export { default as ToastRenderer } from "../components/ToastRenderer.vue";

export const useToast = (): {
    toasts: Ref<ReadonlyArray<Toast>>,
    toast: (options: ToastOption) => void,
} => {
    const data = inject(toastSymbol, () => ({
        toasts: ref(new Array<Toast>(0)),
        counter: 0,
    }), true);

    provide(toastSymbol, data);

    return {
        toasts: ref(data.toasts),
        toast: (options: ToastOption) => {
            const toast: Toast = {
                ...options,
                created: new Date(),
                id: data.counter++,
                close: () => {
                    const i = data.toasts.value.indexOf(toast);
                    if (i !== -1) {
                        data.toasts.value.splice(i, 1);
                    }
                },
                touch: () => {
                    clearTimeout(toast[inner].timeoutHandle);
                    toast[inner].timeoutHandle = setTimeout(
                        () => toast.close(),
                        options.timeout,
                    );
                },
                [inner]: {
                    timeoutHandle: setTimeout(() => 0),
                },
            };

            data.toasts.value.push(toast);

            toast.touch();
        },
    };
};
